import React, { useState } from "react";
import { Share2, Link as LinkIcon, Check, Twitter, Linkedin, Mail } from "lucide-react";

/**
 * Share dropdown. Builds absolute URL from the current origin + `path`,
 * appends an optional `?ref=<code>` (so shares double as referral links),
 * and offers: copy link, X/Twitter, LinkedIn, Email.
 *
 * Usage: <ShareButton path={`/jobs/${id}`} title={job.title} refCode={user?.referral_code} />
 */
export default function ShareButton({ path, title = "", refCode = null, variant = "ghost", label = "Share" }) {
    const [open, setOpen] = useState(false);
    const [copied, setCopied] = useState(false);

    const url = (() => {
        const base = typeof window !== "undefined" ? window.location.origin : "";
        const u = new URL(path, base || "https://example.com");
        if (refCode) u.searchParams.set("ref", refCode);
        return u.toString();
    })();

    const shareText = title ? `${title} — on Project Atlas` : "Project Atlas — the map of human knowledge";

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 1400);
        } catch {}
    };

    const links = [
        {
            key: "twitter",
            label: "X / Twitter",
            icon: Twitter,
            href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(url)}`,
        },
        {
            key: "linkedin",
            label: "LinkedIn",
            icon: Linkedin,
            href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
        },
        {
            key: "email",
            label: "Email",
            icon: Mail,
            href: `mailto:?subject=${encodeURIComponent(shareText)}&body=${encodeURIComponent(url)}`,
        },
    ];

    return (
        <div className="relative inline-block" data-testid="share-btn-wrap">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="arcade-btn"
                data-variant={variant}
                data-testid="share-btn"
            >
                <Share2 className="w-4 h-4" /> {label}
            </button>
            {open && (
                <div
                    className="absolute right-0 mt-2 w-64 crystal rounded-2xl p-2 z-30 shadow-xl"
                    onMouseLeave={() => setOpen(false)}
                    data-testid="share-menu"
                >
                    <button
                        type="button"
                        onClick={copy}
                        data-testid="share-copy-link"
                        className="w-full flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-white/80 text-sm text-slate-800"
                    >
                        {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <LinkIcon className="w-4 h-4" />}
                        <span className="flex-1 text-left font-display font-bold">
                            {copied ? "Copied!" : "Copy link"}
                        </span>
                        {refCode && !copied && (
                            <span className="text-[10px] text-slate-400">+ref</span>
                        )}
                    </button>
                    {links.map((l) => (
                        <a
                            key={l.key}
                            href={l.href}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`share-${l.key}`}
                            className="w-full flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-white/80 text-sm text-slate-800"
                        >
                            <l.icon className="w-4 h-4" />
                            <span className="font-display font-bold">{l.label}</span>
                        </a>
                    ))}
                </div>
            )}
        </div>
    );
}
